import React from 'react';
import { Provider } from 'react-redux';

import type { Store } from '@reduxjs/toolkit';
import { throttle } from 'lodash';

import { StorageAdapterInterface } from './types';

const VERSION_SUFFIX = '::version';

type Props<State> = {
    version: string; // storageVersion
    stateKey: string; // storageKey
    storageAdapter: StorageAdapterInterface;
    createStore: (initialState: Partial<State> | undefined) => Store<State>;
    onInitialState: (props: { state: State | undefined }) => void;
    onStateUpdate: (props: { state: State; setState: (state: Partial<State>) => void }) => void;
};

type State<T extends {}> = {
    store: Store<T> | undefined;
};

class AppStateProvider<T = {}> extends React.PureComponent<Props<T>, State<T>> {
    static defaultProps = {
        loadInitialState: () => Promise.resolve(),
        onStateUpdate: () => undefined,
    };

    state: State<T> = {
        store: undefined,
    };

    componentDidMount() {
        this.clearInvalidatedStorageState()
            .then(() => this.getStorageState())
            .then((initialState) => {
                this.props.onInitialState({ state: initialState });

                const store = this.props.createStore(initialState);

                store.subscribe(
                    throttle(() => {
                        const state = store.getState();
                        this.props.onStateUpdate({
                            state,
                            setState: this.setStorageState,
                        });
                    }, 1000)
                );

                this.setState({ store });
            })
            .catch(console.log);
    }

    componentDidCatch(error: Error, info: {}) {
        console.log(error, info);
    }

    clearInvalidatedStorageState = (): Promise<void> => {
        const { storageAdapter, stateKey, version } = this.props;
        return storageAdapter
            .get<string | undefined>(stateKey + VERSION_SUFFIX)
            .then((lastVersion) => {
                if (lastVersion === version) {
                    return;
                }

                // version invalidated, bump version & clear stored state
                storageAdapter.set(stateKey, null);
                storageAdapter.set(stateKey + VERSION_SUFFIX, version);
            });
    };

    getStorageState = (): Promise<T | undefined> => {
        const { storageAdapter, stateKey } = this.props;
        return storageAdapter.get(stateKey);
    };

    setStorageState = (state: Partial<T>): Promise<boolean> => {
        const { storageAdapter, stateKey } = this.props;
        return storageAdapter.set(stateKey, state);
    };

    render() {
        if (this.state.store == null) {
            // @todo Display something here
            return null;
        }

        return <Provider store={this.state.store}>{this.props.children}</Provider>;
    }
}

export default AppStateProvider;
